
import React, { useEffect, useState, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { Region, PeopleGroup } from '../../../data/types/region-types';
import { formatCurrencyShort } from '../../../data/utils/money-utils';
import { fetchJoshuaProjectData } from '../../../data/utils/joshua-project-api';
import { general_mexico_regions } from '../../../data/mexico/general-regions';
import { getTotalRegionalPledges } from '../../../data/utils/region-utils';

interface FundingLabelsProps {
  map: mapboxgl.Map;
  regions: Region[];
  markerRefs: mapboxgl.Marker[];
  mapZoomLevel?: 'country' | 'region' | 'city';
  selectedRegion: Region | null;
  selectedCity?: any | null;
  showGlobalRegions?: boolean;
  globalData?: PeopleGroup[];
  isMexicoView?: boolean;
  showFunding?: boolean;
}

const FundingLabels: React.FC<FundingLabelsProps> = ({ 
  map, 
  regions, 
  markerRefs,
  mapZoomLevel = 'country',
  selectedRegion,
  selectedCity = null,
  showGlobalRegions = false,
  globalData = [],
  isMexicoView = false,
  showFunding = true
}) => {
  const [labelElements, setLabelElements] = useState<HTMLElement[]>([]);
  const [internalGlobalData, setInternalGlobalData] = useState<PeopleGroup[]>([]);
  const specialRegionMarkersRef = useRef<{[key: string]: mapboxgl.Marker}>({});
  const labelsCreatedRef = useRef<boolean>(false);
  const dataToDisplayRef = useRef<(Region | PeopleGroup | any)[]>([]);
  const viewModeRef = useRef<string>('');
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const mexicoMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const permanentlyAddedRef = useRef<boolean>(false);
  const styleElementRef = useRef<HTMLStyleElement | null>(null);
  const showFundingRef = useRef(showFunding);
  const pendingToggleRef = useRef<boolean | null>(null);
  const forceRecreateRef = useRef<boolean>(false);
  const recreationAttemptsRef = useRef<number>(0);

  // Store markers in a ref to prevent them from being cleaned up incorrectly
  useEffect(() => {
    markersRef.current = markerRefs;
  }, []);

  // Load global data when needed
  useEffect(() => {
    const loadGlobalData = async () => {
      if (globalData.length > 0) {
        setInternalGlobalData(globalData);
        return;
      }
      
      try {
        console.log("FundingLabels: Fetching global data");
        const data = await fetchJoshuaProjectData();
        console.log("FundingLabels: Received global data:", data.length, "items");
        setInternalGlobalData(data);
      } catch (error) {
        console.error('Failed to load global data:', error);
      }
    };
    
    if (showGlobalRegions) {
      loadGlobalData();
    }
  }, [showGlobalRegions, globalData]);

  // Listen for custom event to recreate funding labels - ENHANCED version
  useEffect(() => {
    const handleRecreateLabels = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log("FundingLabels: Received recreate-funding-labels event", customEvent.detail);
      
      // Reset recreation attempts counter when a new request comes in
      recreationAttemptsRef.current = 0;
      
      // If we're being asked to recreate with the current funding state or force recreation
      if ((customEvent.detail?.showFunding !== undefined && 
           customEvent.detail?.showFunding === true) || 
          customEvent.detail?.forceRecreate === true) {
        
        // Reset refs to force recreation
        labelsCreatedRef.current = false;
        permanentlyAddedRef.current = false;
        forceRecreateRef.current = true;
        showFundingRef.current = true; // Make sure we're showing funding
        
        // Remove existing markers to prevent duplicates
        console.log("FundingLabels: Clearing existing markers before recreation");
        mexicoMarkersRef.current.forEach(marker => marker.remove());
        mexicoMarkersRef.current = [];
        
        Object.values(specialRegionMarkersRef.current).forEach(marker => marker.remove());
        specialRegionMarkersRef.current = {};
        
        // Make sure body doesn't have hide-funding class
        document.body.classList.remove('hide-funding');
        
        // Force all existing elements to be visible with inline styles
        const existingFundingElements = document.querySelectorAll('.funding-label, .city-marker-funding, .region-funding-label, .mexico-region-funding, [data-marker-type="funding"]');
        existingFundingElements.forEach(el => {
          (el as HTMLElement).style.visibility = 'visible';
          (el as HTMLElement).style.display = 'block';
          (el as HTMLElement).style.opacity = '1';
          (el as HTMLElement).setAttribute('data-funding-visible', 'true');
        });
        
        // Attempt recreation immediately and then with delays for reliability
        console.log("FundingLabels: Immediate recreation attempt");
        createFundingLabels();
        
        // Schedule multiple attempts with increasing delays
        const delays = [50, 150, 300, 600];
        delays.forEach(delay => {
          setTimeout(() => {
            if (!labelsCreatedRef.current || forceRecreateRef.current) {
              recreationAttemptsRef.current++;
              console.log(`FundingLabels: Delayed recreation attempt ${recreationAttemptsRef.current} after ${delay}ms`);
              createFundingLabels();
            }
          }, delay);
        });
      }
    };
    
    // Add event listener
    document.addEventListener('recreate-funding-labels', handleRecreateLabels);
    
    return () => {
      document.removeEventListener('recreate-funding-labels', handleRecreateLabels);
    };
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      console.log("FundingLabels: Cleanup on component unmount");
      
      // Remove any style elements we added
      if (styleElementRef.current) {
        document.head.removeChild(styleElementRef.current);
        styleElementRef.current = null;
      }
      
      // Clear all markers
      Object.values(specialRegionMarkersRef.current).forEach(marker => marker.remove());
      mexicoMarkersRef.current.forEach(marker => marker.remove());
      specialRegionMarkersRef.current = {};
      mexicoMarkersRef.current = [];
      permanentlyAddedRef.current = false;
    };
  }, []);

  // Add permanent style for Mexico view
  useEffect(() => {
    // Add a global style to ensure Mexico view labels stay visible
    const styleId = 'mexico-funding-labels-force-style';
    
    if (!styleElementRef.current) {
      const styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = `
        /* Force Mexico region markers to stay visible */
        body:not(.hide-funding) .mapboxgl-marker.mexico-region-funding {
          visibility: visible !important;
          opacity: 1 !important;
          z-index: 9999 !important;
          display: block !important;
          pointer-events: none !important;
          position: absolute !important;
        }
        
        /* Ensure all region labels are visible in Mexico view */
        body:not(.hide-funding) .mapboxgl-canvas-container .mexico-view .region-funding-label {
          visibility: visible !important;
          opacity: 1 !important;
          z-index: 9999 !important;
          display: block !important;
        }
        
        /* Hide region funding labels when in region view or zoomed in */
        body .mapboxgl-canvas-container .region-view .region-funding-label,
        body .mapboxgl-canvas-container[data-zoom-level="region"] .region-funding-label,
        body .mapboxgl-canvas-container[data-zoom-level="city"] .region-funding-label,
        body .mapboxgl-canvas-container[data-region="CHIS"][data-zoom-level="region"] .funding-label-container,
        body .mapboxgl-canvas-container[data-region="HGO"][data-zoom-level="region"] .funding-label-container,
        body .mapboxgl-canvas-container[data-region="CHIS"][data-zoom-level="city"] .funding-label-container,
        body .mapboxgl-canvas-container[data-region="HGO"][data-zoom-level="city"] .funding-label-container {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
        
        /* Critical override class for force showing funding regardless of other rules */
        body.force-show-funding .funding-label,
        body.force-show-funding .city-marker-funding,
        body.force-show-funding .region-funding-label,
        body.force-show-funding .mexico-region-funding,
        body.force-show-funding .mapboxgl-marker[data-marker-type="funding"],
        body.force-show-funding .mapboxgl-marker.region-funding-label,
        body.force-show-funding [data-toggle-responsive="true"] {
          visibility: visible !important;
          opacity: 1 !important;
          display: block !important;
          z-index: 9999 !important;
          pointer-events: auto !important;
        }
        
        /* Ensure all transition labels stay visible when not hidden */
        body:not(.hide-funding) .funding-label-container,
        body:not(.hide-funding) .funding-label-text,
        body:not(.hide-funding) .region-funding-label,
        body:not(.hide-funding) .mexico-region-funding,
        body:not(.hide-funding) [data-marker-type="funding"],
        body:not(.hide-funding) [data-toggle-responsive="true"] {
          visibility: visible !important;
          opacity: 1 !important;
          display: block !important;
          transition: transform 0.3s ease-out !important;
          will-change: transform !important;
          backface-visibility: hidden !important;
        }
        
        /* Critical override for special regions */
        body:not(.hide-funding) .region-funding-chiapas,
        body:not(.hide-funding) .region-funding-hidalgo,
        body:not(.hide-funding) .chis-funding,
        body:not(.hide-funding) .hgo-funding,
        body:not(.hide-funding) [data-special-region] {
          visibility: visible !important;
          opacity: 1 !important;
          display: block !important;
        }
        
        /* Hide funding labels when funding toggle is off */
        body.hide-funding .funding-label-container,
        body.hide-funding .funding-label-text,
        body.hide-funding .region-funding-label,
        body.hide-funding .mexico-region-funding,
        body.hide-funding .mapboxgl-marker.funding-label,
        body.hide-funding .mapboxgl-marker.region-funding-label,
        body.hide-funding .mapboxgl-marker.mexico-region-funding,
        body.hide-funding .region-funding-chiapas,
        body.hide-funding .region-funding-hidalgo,
        body.hide-funding .chis-funding,
        body.hide-funding .hgo-funding,
        body.hide-funding [data-marker-type="funding"],
        body.hide-funding [data-toggle-responsive="true"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
        
        /* Special class to force a reflow on funding toggle */
        .funding-toggle-update {
          animation: funding-toggle-reflow 0.01s;
        }
        @keyframes funding-toggle-reflow {
          0% { opacity: 0.99; }
          100% { opacity: 1; }
        }
      `;
      
      document.head.appendChild(styleElement);
      styleElementRef.current = styleElement;
    }
  }, []);

  // Force recreation of labels when switching between global and Mexico views
  useEffect(() => {
    if (showGlobalRegions !== isMexicoView) {
      console.log("FundingLabels: View switched between global and Mexico, resetting labels");
      labelsCreatedRef.current = false;
      permanentlyAddedRef.current = false;
      
      // Clear markers when switching views
      mexicoMarkersRef.current.forEach(marker => marker.remove());
      mexicoMarkersRef.current = [];
      
      Object.values(specialRegionMarkersRef.current).forEach(marker => marker.remove());
      specialRegionMarkersRef.current = {};
    }
  }, [showGlobalRegions, isMexicoView]);

  // Hide or show labels based on selected region and zoom level
  useEffect(() => {
    if (map) {
      const container = map.getContainer();
      
      if (container) {
        // Add zoom level as a data attribute to the container
        container.setAttribute('data-zoom-level', mapZoomLevel);
        
        if (selectedRegion) {
          // When a region is selected, hide all regional funding labels
          container.classList.add('region-view');
          container.setAttribute('data-region', selectedRegion.region_id);
          
          // If we're in Chiapas or Hidalgo and zoomed in, add special class
          if ((selectedRegion.region_id === 'CHIS' || selectedRegion.region_id === 'HGO') && 
              (mapZoomLevel === 'region' || mapZoomLevel === 'city')) {
            container.classList.add('hide-funding-labels');
          } else {
            container.classList.remove('hide-funding-labels');
          }
        } else {
          // When no region is selected, show regional funding labels
          container.classList.remove('region-view', 'hide-funding-labels');
          container.removeAttribute('data-region');
        }
      }
    }
    
    return () => {
      // Cleanup when component unmounts or dependencies change
      if (map) {
        const container = map.getContainer();
        if (container) {
          container.classList.remove('region-view', 'hide-funding-labels');
          container.removeAttribute('data-zoom-level');
          container.removeAttribute('data-region');
        }
      }
    };
  }, [selectedRegion, map, mapZoomLevel]);

  // Effect to handle showFunding toggle - ENHANCED with better marker recreation
  useEffect(() => {
    if (map) {
      console.log("FundingLabels: showFunding changed to", showFunding);
      
      // Store the current value for comparison
      const previousValue = showFundingRef.current;
      const changed = previousValue !== showFunding;
      showFundingRef.current = showFunding;
      
      // Update body class
      const body = document.body;
      if (!showFunding) {
        body.classList.add('hide-funding');
        
        // When funding is hidden, mark that we need to recreate labels when shown again
        if (changed && previousValue === true) {
          console.log("FundingLabels: Funding turned off, will need to recreate labels when turned back on");
          labelsCreatedRef.current = false;
          permanentlyAddedRef.current = false;
        }
      } else {
        body.classList.remove('hide-funding');
        
        // When funding is shown and it was previously off, force label recreation
        if (changed && previousValue === false) {
          console.log("FundingLabels: Funding turned back on, forcing label recreation");
          forceRecreateRef.current = true;
          
          // Reset flags to force recreation
          labelsCreatedRef.current = false;
          permanentlyAddedRef.current = false;
          
          // Immediately force visibility of any existing markers
          const existingFundingElements = document.querySelectorAll('.funding-label, .city-marker-funding, .region-funding-label, .mexico-region-funding, [data-marker-type="funding"]');
          existingFundingElements.forEach(el => {
            (el as HTMLElement).style.visibility = 'visible';
            (el as HTMLElement).style.display = 'block';
            (el as HTMLElement).style.opacity = '1';
            (el as HTMLElement).setAttribute('data-funding-visible', 'true');
          });
          
          // Force show funding with special class
          document.body.classList.add('force-show-funding');
          setTimeout(() => {
            document.body.classList.remove('force-show-funding');
          }, 200);
          
          // Immediate attempt to recreate labels
          createFundingLabels();
          
          // Staggered attempts for reliability
          setTimeout(() => createFundingLabels(), 100);
          setTimeout(() => createFundingLabels(), 300);
        }
      }
      
      // If this was triggered by a state change, force refresh markers
      if (changed) {
        pendingToggleRef.current = showFunding;
        console.log("FundingLabels: Scheduling marker refresh due to toggle change");
        
        // Apply funding visibility to all markers
        mexicoMarkersRef.current.forEach(marker => {
          marker.getElement().setAttribute('data-funding-visible', String(showFunding));
          if (showFunding) {
            marker.getElement().style.visibility = 'visible';
            marker.getElement().style.display = 'block';
            marker.getElement().style.opacity = '1';
          }
        });
        
        Object.values(specialRegionMarkersRef.current).forEach(marker => {
          marker.getElement().setAttribute('data-funding-visible', String(showFunding));
          if (showFunding) {
            marker.getElement().style.visibility = 'visible';
            marker.getElement().style.display = 'block';
            marker.getElement().style.opacity = '1';
          }
        });
        
        // Force a reflow
        const container = map.getContainer();
        container.classList.add('funding-toggle-update');
        setTimeout(() => {
          container.classList.remove('funding-toggle-update');
          pendingToggleRef.current = null;
        }, 50);
      }
    }
  }, [showFunding, map]);

  // Create funding labels function - extracted for reuse and enhanced for better toggling
  const createFundingLabels = () => {
    if (!map) {
      console.log("FundingLabels: Cannot create labels - map is not ready");
      return;
    }
    
    console.log("FundingLabels: Creating labels with view mode:", isMexicoView ? "Mexico" : "Global");
    console.log("FundingLabels: Selected region:", selectedRegion?.region_id || "none");
    console.log("FundingLabels: showFunding state:", showFunding);
    console.log("FundingLabels: forceRecreate flag:", forceRecreateRef.current);
    
    // Should we show regional funding based on current view?
    const shouldShowRegionalFunding = mapZoomLevel === 'country' && !selectedRegion;
    
    if (!shouldShowRegionalFunding) {
      console.log("FundingLabels: Not showing regional funding in this view");
      return;
    }
    
    // Generate a view mode string for comparison
    const currentViewMode = `${isMexicoView ? 'mexico' : 'global'}-${showGlobalRegions ? 'global' : 'mexico'}`;
    
    // Only skip if we're not forcing recreation and other conditions are met
    if (!forceRecreateRef.current && viewModeRef.current === currentViewMode && 
        labelsCreatedRef.current && permanentlyAddedRef.current && showFunding === showFundingRef.current) {
      console.log("FundingLabels: Labels already created for this view mode, skipping");
      return;
    }
    
    // Reset force recreate flag after using it
    const wasForced = forceRecreateRef.current;
    forceRecreateRef.current = false;
    
    // Prepare the data to display
    let dataToDisplay: (Region | PeopleGroup | any)[] = [];
    
    if (showGlobalRegions) {
      dataToDisplay = [...internalGlobalData].filter(item => 
        !((item.region_id === 'CHIS' || item.region_id === 'HGO'))
      );
      console.log(`FundingLabels: Global regions to display: ${dataToDisplay.length}`);
    } else if (isMexicoView) {
      console.log("FundingLabels: Preparing Mexico regions data");
      const allRegions = [
        ...regions,
        ...general_mexico_regions
      ].map(region => ({ 
        ...region, 
        isRegion: true,
        pledges: getTotalRegionalPledges(region)
      }));
      
      // Deduplicate regions
      const regionMap = new Map();
      allRegions.forEach(region => {
        if (!regionMap.has(region.region_id)) {
          regionMap.set(region.region_id, region);
        }
      });
      
      dataToDisplay = Array.from(regionMap.values());
      console.log("FundingLabels: Mexico regions to display:", dataToDisplay.length);
    }
    
    // Store in ref to prevent recreation
    dataToDisplayRef.current = dataToDisplay;
    
    // Update the view mode reference
    viewModeRef.current = currentViewMode;

    // Create new labels
    console.log("FundingLabels: Creating new labels for", dataToDisplay.length, "regions, showFunding =", showFunding);
    
    // Clear existing markers of the appropriate type (based on view)
    if (isMexicoView) {
      console.log("FundingLabels: Clearing existing Mexico markers before recreation");
      mexicoMarkersRef.current.forEach(marker => marker.remove());
      mexicoMarkersRef.current = [];
    } else if (wasForced) {
      // If this was a forced recreation, clear ALL markers
      console.log("FundingLabels: FORCED clear of ALL markers");
      mexicoMarkersRef.current.forEach(marker => marker.remove());
      mexicoMarkersRef.current = [];
      
      Object.values(specialRegionMarkersRef.current).forEach(marker => marker.remove());
      specialRegionMarkersRef.current = {};
      
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
    } else {
      // Normal clearing of view-specific markers
      console.log("FundingLabels: Clearing existing global markers");
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
    }
    
    console.log(`FundingLabels: After clearing, ${markersRef.current.length} regular markers, ${mexicoMarkersRef.current.length} Mexico markers remain`);
    
    // Create markers for each region with pledges
    dataToDisplay.forEach(item => {
      if ('pledges' in item && item.coordinates && Array.isArray(item.coordinates) && item.coordinates.length === 2) {
        const formattedPledges = formatCurrencyShort(item.pledges);
        const itemName = item.name || item.english_name || 'Unknown';
        const isRegionItem = !!item.isRegion;
        const regionId = item.region_id || '';
        
        console.log(`Creating funding label for ${itemName}: ${formattedPledges}, visible: ${showFunding}`);
        
        const fundingLabel = document.createElement('div');
        fundingLabel.className = 'region-funding-label funding-label';
        
        if (isMexicoView) {
          fundingLabel.className += ' mexico-view-label mexico-region-funding';
        }
        
        fundingLabel.setAttribute('data-region-id', regionId);
        fundingLabel.setAttribute('data-view-mode', currentViewMode);
        fundingLabel.setAttribute('data-amount', formattedPledges);
        fundingLabel.setAttribute('data-marker-type', 'funding');
        fundingLabel.setAttribute('data-funding-visible', String(showFunding));
        fundingLabel.setAttribute('data-toggle-responsive', 'true');
        
        // Set visibility based on current funding state
        fundingLabel.style.visibility = showFunding ? 'visible' : 'hidden';
        fundingLabel.style.display = showFunding ? 'block' : 'none';
        fundingLabel.style.opacity = showFunding ? '1' : '0';
        
        fundingLabel.innerHTML = `
          <div class="funding-label-container ${isRegionItem ? 'region-funding' : ''} ${regionId.toLowerCase()}-funding">
            <span class="funding-label-text">${formattedPledges}</span>
          </div>
        `;

        if (isRegionItem && (regionId === 'CHIS' || regionId === 'HGO')) {
          const regionClass = regionId === 'CHIS' ? 'region-funding-chiapas' : 'region-funding-hidalgo';
          fundingLabel.className += ` ${regionClass}`;
          fundingLabel.setAttribute('data-special-region', regionId);
        }

        try {
          const marker = new mapboxgl.Marker({
            element: fundingLabel,
            anchor: 'center',
            offset: [0, 0],
            pitchAlignment: 'map',
            rotationAlignment: 'map'
          })
            .setLngLat(item.coordinates)
            .addTo(map);
            
          // Add data attributes and styles for better targeting
          marker.getElement().setAttribute('data-marker-type', 'funding');
          marker.getElement().setAttribute('data-toggle-responsive', 'true');
          marker.getElement().style.visibility = showFunding ? 'visible' : 'hidden';
          marker.getElement().style.display = showFunding ? 'block' : 'none';
          marker.getElement().style.opacity = showFunding ? '1' : '0';
            
          if (isMexicoView) {
            // For Mexico view, use a separate ref to prevent these markers from being removed
            marker.getElement().classList.add('mexico-region-funding');
            mexicoMarkersRef.current.push(marker);
          } else if (isRegionItem && (regionId === 'CHIS' || regionId === 'HGO')) {
            specialRegionMarkersRef.current[regionId] = marker;
          } else {
            markersRef.current.push(marker);
          }
        } catch (error) {
          console.error(`Failed to create marker for ${itemName}:`, error);
        }
      }
    });

    labelsCreatedRef.current = true;
    permanentlyAddedRef.current = true;
    
    console.log(`FundingLabels: Created ${markersRef.current.length} regular markers, ${mexicoMarkersRef.current.length} Mexico markers, and ${Object.keys(specialRegionMarkersRef.current).length} special markers`);
    
    // Force visibility again after creation if showing funding
    if (showFunding) {
      const allNewElements = document.querySelectorAll('.funding-label, .city-marker-funding, .region-funding-label, .mexico-region-funding, [data-marker-type="funding"]');
      allNewElements.forEach(el => {
        (el as HTMLElement).style.visibility = 'visible';
        (el as HTMLElement).style.display = 'block';
        (el as HTMLElement).style.opacity = '1';
        (el as HTMLElement).setAttribute('data-funding-visible', 'true');
      });
    }
  };

  // Main effect for creating funding labels - this runs once per view type
  useEffect(() => {
    createFundingLabels();
  }, [map, regions, internalGlobalData, mapZoomLevel, selectedRegion, showGlobalRegions, isMexicoView, showFunding]);
  
  // Add an additional effect to ensure Mexico view labels stay visible
  useEffect(() => {
    if (isMexicoView && map && showFunding) {
      console.log("FundingLabels: Ensuring Mexico view labels remain visible");
      
      // Refresh the visibility of labels for Mexico view
      setTimeout(() => {
        mexicoMarkersRef.current.forEach(marker => {
          const element = marker.getElement();
          element.style.visibility = 'visible';
          element.style.display = 'block';
          element.style.opacity = '1';
          element.style.zIndex = '9999';
          // Add data attributes for CSS targeting
          element.setAttribute('data-marker-type', 'funding');
          element.setAttribute('data-funding-visible', 'true');
          element.setAttribute('data-toggle-responsive', 'true');
        });
      }, 100);
    }
  }, [isMexicoView, map, showFunding]);
  
  // NEW: Special handling for Chiapas and Hidalgo regions
  useEffect(() => {
    if (!map) return;
    
    // Apply special attributes to Chiapas and Hidalgo markers for better CSS targeting
    Object.entries(specialRegionMarkersRef.current).forEach(([regionId, marker]) => {
      const element = marker.getElement();
      element.setAttribute('data-special-region', regionId);
      element.setAttribute('data-marker-type', 'funding');
      element.setAttribute('data-toggle-responsive', 'true');
      
      if (regionId === 'CHIS') {
        element.classList.add('region-funding-chiapas');
      } else if (regionId === 'HGO') {
        element.classList.add('region-funding-hidalgo');
      }
      
      // Set visibility based on current funding state
      if (showFunding) {
        element.style.visibility = 'visible';
        element.style.display = 'block';
        element.style.opacity = '1';
      } else {
        element.style.visibility = 'hidden';
        element.style.display = 'none';
        element.style.opacity = '0';
      }
    });
    
  }, [map, showFunding]);

  // NEW: Add force refresh effect when nothing else works
  useEffect(() => {
    if (!showFunding || !map) return;
    
    const forceRefresh = () => {
      // Run only for Mexico and global views
      if (!selectedRegion && mapZoomLevel === 'country') {
        console.log("FundingLabels: Force refresh effect running");
        
        // Make sure the body class is correct
        document.body.classList.remove('hide-funding');
        
        // Double check that all funding elements are visible
        const allElements = document.querySelectorAll('.funding-label, .city-marker-funding, .region-funding-label, .mexico-region-funding, [data-marker-type="funding"]');
        if (allElements.length === 0 || forceRecreateRef.current) {
          console.log("FundingLabels: No funding elements found or force flag set, recreating");
          
          // Reset creation flags
          labelsCreatedRef.current = false;
          permanentlyAddedRef.current = false;
          
          // Force recreation
          createFundingLabels();
        } else {
          console.log(`FundingLabels: Found ${allElements.length} funding elements, ensuring visibility`);
          allElements.forEach(el => {
            (el as HTMLElement).style.visibility = 'visible';
            (el as HTMLElement).style.display = 'block';
            (el as HTMLElement).style.opacity = '1';
            (el as HTMLElement).setAttribute('data-funding-visible', 'true');
          });
        }
      }
    };
    
    // Run on mount and when dependencies change
    forceRefresh();
    
    // Then run with delay for reliability
    const timer = setTimeout(forceRefresh, 500);
    return () => clearTimeout(timer);
  }, [map, selectedRegion, mapZoomLevel, showFunding]);
  
  return null;
};

export default FundingLabels;
